import { CopyOutlined, TeamOutlined } from '@ant-design/icons'
import { usePartners, type Partner } from '@entities/partner'
import type { FormProps } from 'antd'
import { App, Button, Card, Col, Form, Input, Row, Space, theme, Typography } from 'antd'
import { useEffect } from 'react'
import InputMask from 'react-input-mask'

const { Text } = Typography

interface MainFormProps {
    partner: Partner
    isLinkedPartner: boolean
}

type MainFormFields = {
    name: string
    tgUsername: string
    email: string
    phone: string
    comment: string
}

export const MainFormMobile = ({ partner, isLinkedPartner }: MainFormProps) => {
    const {
        actions: { editPartner, unlinkPartner }
    } = usePartners()

    const {
        token: { colorWhite }
    } = theme.useToken()

    const onFinish: FormProps<MainFormFields>['onFinish'] = async (values) => {
        editPartner({ id: partner.id, data: { ...partner, ...values } })
    }

    const [form] = Form.useForm()

    useEffect(() => {
        form.setFieldsValue({
            name: partner.name,
            tgUsername: partner.tgUsername,
            email: partner.email,
            phone: partner.phone,
            comment: partner.comment
        })
    }, [form, partner])

    const { message } = App.useApp()

    return (
        <Form
            form={form}
            name="edit-partner"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            onFinish={onFinish}
            initialValues={{
                name: partner.name,
                tgUsername: partner.tgUsername,
                email: partner.email,
                phone: partner.phone,
                comment: partner.comment
            }}
            onValuesChange={(val) => console.log(val)}
        >
            <Card size="small" style={{ backgroundColor: colorWhite, marginBottom: '16px' }}>
                <Row gutter={16}>
                    {partner?.linkedPartnerId ? (
                        <Col span={24}>
                            <Space>
                                Пользователь привязан к партнеру
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={() =>
                                        partner.id &&
                                        unlinkPartner({
                                            partnerId: partner.id
                                        })
                                    }
                                >
                                    Отвязать
                                </Button>
                            </Space>
                        </Col>
                    ) : (
                        <>
                            <Col span={'24'} style={{ marginBottom: '10px' }}>
                                <Text style={{ fontSize: '17px', color: '#8F8E94' }}>Пригласить в Telegaplan</Text>
                                <Space.Compact style={{ width: '100%', marginTop: '10px' }}>
                                    <Input
                                        value={`Я приглашаю тебя в ${window.location.origin}, самую удобную Crm для Telegram.`}
                                        size="large"
                                    />
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={() =>
                                            navigator.clipboard
                                                .writeText(
                                                    `Я приглашаю тебя в ${window.location.origin}, самую удобную Crm для Telegram.`
                                                )
                                                .then(() => {
                                                    message.success('Скопировано в буфер обмена')
                                                })
                                        }
                                    >
                                        <CopyOutlined />
                                    </Button>
                                </Space.Compact>
                            </Col>
                            <Col span={24}>
                                <Space>
                                    <Text style={{ fontSize: '17px', color: '#8F8E94' }}>
                                        Создать связку с пользователем Telepaglan
                                    </Text>
                                    <Button
                                        type="primary"
                                        icon={<TeamOutlined />}
                                        size="large"
                                        onClick={() =>
                                            navigator.clipboard
                                                .writeText(`${window.location.origin}?partner_id=${partner?.id}`)
                                                .then(() => {
                                                    message.success('Скопировано в буфер обмена')
                                                })
                                        }
                                    />
                                </Space>
                            </Col>
                        </>
                    )}
                </Row>
            </Card>
            <Card size="small" style={{ backgroundColor: colorWhite }}>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>Имя</Text>}
                            name="name"
                            required={false}
                            rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                            <Input placeholder="Введите имя" readOnly={isLinkedPartner} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>Username в телеграм</Text>}
                            name="tgUsername"
                            required={false}
                            rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                            <Input placeholder="@username" readOnly={isLinkedPartner} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>E-mail</Text>}
                            name="email"
                            required={false}
                            rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                            <Input placeholder="helle@telegaplan.me" readOnly={isLinkedPartner} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>Номер телефона</Text>}
                            name="phone"
                            required={false}
                            rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                            <InputMask mask="+7(999)999-99-99" readOnly={isLinkedPartner}>
                                {(inputProps) => (
                                    <Input {...inputProps} placeholder="Введите номер телефона" size="large" />
                                )}
                            </InputMask>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>Комментарий</Text>}
                            name="comment"
                            required={false}
                        >
                            <Input.TextArea placeholder="Введите текст" readOnly={isLinkedPartner} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <div
                            style={{
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                padding: '8px 16px 42px 16px',
                                zIndex: 10000,
                                width: '100%',
                                backgroundColor: colorWhite,
                                borderTop: '0.5px solid #D3D3D5'
                            }}
                        >
                            <Button
                                type="primary"
                                htmlType="submit"
                                disabled={isLinkedPartner}
                                size="large"
                                style={{ width: '100%' }}
                            >
                                Сохранить
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Card>
        </Form>
    )
}
